import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Ionicons, Feather } from '@expo/vector-icons';
import { Paprika, PaprikaShadows } from '../../styles/theme';

interface Props {
  title: string;
  /** Display servings; omit to hide the right-side reminder. */
  servings?: number | string | null;
  /** 'quit' = Feather x + "Quitter" (cooking) · 'back' = chevron + "Retour" (planning). */
  variant?: 'quit' | 'back';
  /** Mobile: round icon-only button, smaller title, no label. */
  compact?: boolean;
  onLeftPress: () => void;
}

/**
 * Shared top bar for the cooking flow (handoff "barre haute"):
 * left pill button · recipe title centered (Newsreader italic) · flame + portions.
 * No step count here — step context lives in the eyebrow and the rail.
 */
export const CookingTopBar: React.FC<Props> = ({
  title,
  servings,
  variant = 'quit',
  compact = false,
  onLeftPress,
}) => {
  const { t } = useTranslation();
  const icon =
    variant === 'quit' ? (
      <Feather name="x" size={compact ? 17 : 16} color={Paprika.textPrimary} />
    ) : (
      <Feather name="chevron-left" size={compact ? 18 : 16} color={Paprika.textPrimary} />
    );

  return (
    <View style={[styles.bar, compact ? styles.barCompact : styles.barDesktop]}>
      <TouchableOpacity
        style={compact ? styles.roundBtn : styles.pillBtn}
        onPress={onLeftPress}
        hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
      >
        {icon}
        {!compact && (
          <Text style={styles.pillText}>{t(variant === 'quit' ? 'cooking.quit' : 'navigation.back')}</Text>
        )}
      </TouchableOpacity>

      <Text style={[styles.title, compact && styles.titleCompact]} numberOfLines={1}>
        {title}
      </Text>

      {servings ? (
        <View style={styles.servings}>
          <Ionicons name="flame" size={compact ? 14 : 15} color={Paprika.textSecondary} />
          <Text style={styles.servingsText}>
            {compact
              ? String(servings).match(/\d+/)?.[0] ?? String(servings)
              : typeof servings === 'number'
                ? `${servings} portions`
                : String(servings)}
          </Text>
        </View>
      ) : (
        <View style={compact ? styles.spacerCompact : styles.spacer} />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  bar: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  barDesktop: {
    paddingVertical: 18,
    paddingHorizontal: 28,
    borderBottomWidth: 1,
    borderBottomColor: Paprika.hairline,
  },
  barCompact: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    gap: 12,
  },
  pillBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    height: 40,
    paddingHorizontal: 15,
    borderRadius: 11,
    backgroundColor: Paprika.cardBg,
    borderWidth: 1,
    borderColor: Paprika.border,
    ...PaprikaShadows.card,
  },
  pillText: {
    fontSize: 13.5,
    fontFamily: 'Inter-SemiBold',
    fontWeight: '600',
    color: Paprika.textPrimary,
  },
  roundBtn: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Paprika.cardBg,
    borderWidth: 1,
    borderColor: Paprika.border,
  },
  title: {
    flex: 1,
    textAlign: 'center',
    fontFamily: 'Newsreader-Italic',
    fontStyle: 'italic',
    fontSize: 20,
    color: Paprika.textPrimary,
  },
  titleCompact: {
    fontSize: 17,
  },
  servings: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
  },
  servingsText: {
    fontSize: 13,
    fontFamily: 'Inter-Regular',
    color: Paprika.textSecondary,
  },
  spacer: {
    width: 88,
  },
  spacerCompact: {
    width: 36,
  },
});
